import { useTranslation, type TranslationKey } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import noTeam from "@Assets/images/noTeamIlustrations.svg";
import { useSelector } from "react-redux";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { RootState } from "@Reducers/index";
import { CREATOR_PROJECT_ROLE } from "@Helpers/roles";

/** One user picked for a project role, as the add-users flow stores them. */
type SelectedTeamUser = { userId: string; fullName?: string; profilePic?: string };

interface TeamDetailsTemplateProps {
  /** Role id → the users assigned to it. Keyed by `roles[].roleId`. */
  selectedAddUserDetails: Record<string, SelectedTeamUser[]>;
  /**
   * The roles to summarise, in display order — one section per entry, so this is what decides which
   * roles appear. A list rather than a `roleName → id` record because the order is meaningful and
   * because `Object.keys` widens keys to `string`, which would cost `roleName` the literal type
   * `t()` needs. Nothing is hard-coded here: add a role to the parent's list and it renders, needing
   * only its locale key.
   */
  roles: readonly { roleName: TranslationKey; roleId: string }[];
  openAddUsersModal: () => void;
  handleNextStage: (e: React.MouseEvent<HTMLElement>) => void;
  /** Stamped onto the Next button as `data-index` and read back off the event by the parent. */
  index: number;
}

function TeamDetailsTemplate({
  selectedAddUserDetails,
  openAddUsersModal,
  roles,
  handleNextStage,
  index,
}: TeamDetailsTemplateProps) {
  const { t } = useTranslation();
  const userDetails = useSelector((state: RootState) => state.userDetails);

  const getCurrentUserFullName = () =>
    userDetails.lastName && userDetails.lastName.trim()
      ? `${userDetails.firstName} ${userDetails.lastName}`
      : userDetails.firstName;

  const teamUserTagWrapper = () => (
    <>
      <span className="pt-5 text-base text-[#192048]">
        {t("Great, you've added some members to the project team. You can also")}{" "}
        <span className="cursor-pointer text-start text-base font-semibold text-[#6bbbe3]" onClick={openAddUsersModal}>
          {t("assign more users to Project team")}
        </span>
      </span>
      {roles.map(({ roleName, roleId }) => {
        const isCreatorRole = roleId === String(CREATOR_PROJECT_ROLE.id);
        const hasUsers = Object.hasOwn(selectedAddUserDetails, roleId);
        if (!isCreatorRole && !hasUsers) return null;
        return (
          <div key={roleId} className="flex flex-col gap-5 mt-5">
            <span className="text-base font-semibold text-[#192048]">{t(roleName)}</span>
            <div className="flex flex-wrap gap-5">
              {isCreatorRole && getUserAvatar(userDetails.id, getCurrentUserFullName(), userDetails.profilePic)}
              {teamDetailsViewer(roleId)}
            </div>
          </div>
        );
      })}
      <div className="pt-5">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} data-index={index} onClick={handleNextStage}>
          {t("Next")}
        </ButtonComponent>
      </div>
    </>
  );

  /**
   * DEAD PARAMS: `_fullName` / `_profilePic` are passed by both call sites but never forwarded —
   * `UserNameTagComponent` resolves the user from `id` itself. Declared so the calls keep checking.
   */
  const getUserAvatar = (id?: string, _fullName?: string, _profilePic?: string) => <UserNameTagComponent id={id} />;

  const teamDetailsViewer = (roleId: string) => (
    <>{selectedAddUserDetails[roleId]?.map((item) => getUserAvatar(item.userId, item.fullName, item.profilePic))}</>
  );

  return (
    <div className="flex flex-col px-18">
      {Object.keys(selectedAddUserDetails).length ? (
        teamUserTagWrapper()
      ) : (
        <div className="flex flex-col gap-10">
          <span className="pt-1 text-sm" data-testid="addTeamDesc">
            {t(
              "Select the users you want to add to this project. These users will be added to the Default space. Add them to other spaces through project settings or space settings.",
            )}
          </span>
          <div className="flex flex-col items-center gap-4" data-testid="teamList">
            <div data-testid="teamListdetails">
              <img data-testid="teamListImg" src={noTeam} alt="No Image to show" />
            </div>
            <div className="flex justify-center gap-2.5 items-center">
              <span
                className="cursor-pointer text-start text-base font-semibold text-[#6bbbe3]"
                onClick={openAddUsersModal}
                data-testid="addUsersFromPortalTexts"
              >
                {t("Add Users From Portal")}
              </span>
            </div>
          </div>
          <div className="flex gap-10 pb-4">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              disabled={!selectedAddUserDetails.length}
              onClick={handleNextStage}
              data-index={index}
              data-testid="saveBtnAddTeam"
            >
              {t("Save")}
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={handleNextStage}
              data-index={index}
              data-testid="skipBtnAddTeam"
            >
              {t("Skip")}
            </ButtonComponent>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamDetailsTemplate;
